/**
 * cart-drawer.js — <cart-drawer> web component
 *
 * Checklist coverage:
 *  1  44px tap targets ................... enforced in cart-drawer.css
 *  2  BF-cache / back-forward ............ pageshow listener
 *  3  Subtotal instant update ............ SRA refresh
 *  4  Checkout never pushed below fold ... flex-column CSS + dvh
 *  5  Close: ×, Escape, overlay .......... _onClick + _onKeyDown
 *  6  Custom sections survive re-render .. all JS is delegated from this element;
 *                                          cart-drawer:rendered event for 3rd-party re-init
 *  7  Discount + mobile keyboard ......... dvh panel shrinks; footer stays pinned
 *  8  Multi-currency thresholds .......... threshold stored in pence, SRA re-renders
 *                                          server-side so Liquid money filter is always correct
 *  9  Custom-form ATC .................... listens to document 'cart:updated'
 * 10  Mobile scroll; checkout reachable .. dvh + flex layout in CSS
 * 11  Empty state on last removal ......... SRA re-render
 * 12  Express checkout ................... Shopify.PaymentButton.init() after refresh
 * 13  Focus trap; return focus ........... _updateFocusTrap, close()
 * 14  Free gift / shipping bar ........... covered by cart-progress-bar / cart-free-gift
 * 15  No-JS fallback ..................... /cart still works without JS
 * 17  BNPL messaging ..................... @app blocks in section schema
 * 18  Out-of-stock error ................. 422 -> _showItemError
 * 19  Line items all functional .......... delegated click/input handlers
 * 20  PDP AJAX ATC ....................... product.js dispatches cart:updated
 * 21  One source component ............... single section + snippets
 * 22  Opens from anywhere ................ document 'cart:updated'
 * 23  Order note + cart attributes ........ _persistNote, _persistAttributes,
 *                                          _flushPendingWrites before any mutation
 * 24  Multi-currency prices .............. Liquid money filter; SRA server-side render
 * 25  Qty stepper inventory error ......... 422 -> _showItemError
 * 26  Third-party app compat .............. cart-drawer:rendered custom event
 * 27  Smooth animation ................... CSS
 * 28  Subscription line items ............ shown in cart-line-items.liquid
 * 29  Cart attribute blocks .............. cart-attributes.liquid + section blocks schema
 * 30  Trust badges, delivery estimate .... cart-reassurance.liquid
 * 31  AJAX + SRA ......................... fetch + /?sections=
 * 32  Upsell row ......................... cart-upsell.liquid + _upsellAdd
 */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this._sectionId     = this.dataset.sectionId || 'cart-drawer';
    this._discountCode  = null;
    this._noteTimer     = null;
    this._attrTimers    = {};
    this._refreshing    = false;
    this._previousFocus = null;

    // Named bound references — required so disconnectedCallback can remove them
    this._boundCartUpdated = this._onCartUpdated.bind(this);
    this._boundCartOpening = this._onCartOpening.bind(this);
    this._boundKeyDown     = this._onKeyDown.bind(this);
    this._boundDocClick    = this._onDocClick.bind(this);
    this._boundPageshow    = this._onPageshow.bind(this);

    // Delegated events on <cart-drawer> itself — survive every innerHTML swap
    this.addEventListener('click',  this._onClick.bind(this));
    this.addEventListener('change', this._onChange.bind(this));
    this.addEventListener('submit', this._onSubmit.bind(this));
    this.addEventListener('input',  this._onInput.bind(this));

    document.addEventListener('cart:updated', this._boundCartUpdated);
    document.addEventListener('cart:opening', this._boundCartOpening);
    document.addEventListener('keydown',      this._boundKeyDown);
    document.addEventListener('click',        this._boundDocClick);
    window.addEventListener('pageshow',       this._boundPageshow);

    // Behaviour that used to live in <script> tags inside the drawer markup.
    // Those tags sit in regions this component replaces via DOMParser, and
    // parsed-then-inserted scripts never execute — so after the first cart
    // update the terms gate stopped listening (leaving checkout permanently
    // disabled) and the recommendation row stopped loading.
    this._syncTermsGate();
    this._loadUpsellRecommendations();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this._boundCartUpdated);
    document.removeEventListener('cart:opening', this._boundCartOpening);
    document.removeEventListener('keydown',      this._boundKeyDown);
    document.removeEventListener('click',        this._boundDocClick);
    window.removeEventListener('pageshow',       this._boundPageshow);
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  open() {
    if (this.getAttribute('aria-hidden') === 'false') return;
    this._previousFocus = document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      this.querySelector('.cart-drawer__panel')?.focus({ preventScroll: true });
      this._updateFocusTrap();
    });
  }

  close() {
    if (this.getAttribute('aria-hidden') === 'true') return;
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this._previousFocus?.focus?.({ preventScroll: true });
  }

  // ---------------------------------------------------------------------------
  // Focus trap (item 13)
  // ---------------------------------------------------------------------------

  _updateFocusTrap() {
    const panel = this.querySelector('.cart-drawer__panel');
    if (!panel) return;
    const all = Array.from(panel.querySelectorAll(
      'a[href],' +
      'button:not([disabled]):not([aria-disabled="true"]),' +
      'input:not([disabled]),' +
      'select:not([disabled]),' +
      'textarea:not([disabled]),' +
      '[tabindex]:not([tabindex="-1"])'
    )).filter(el => el.offsetParent !== null);
    this._focusFirst = all[0]              || null;
    this._focusLast  = all[all.length - 1] || null;
  }

  _onKeyDown(e) {
    if (this.getAttribute('aria-hidden') === 'true') return;
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab' || !this._focusFirst || !this._focusLast) return;
    const panel = this.querySelector('.cart-drawer__panel');
    if (e.shiftKey) {
      if (document.activeElement === this._focusFirst || document.activeElement === panel) {
        e.preventDefault();
        this._focusLast.focus();
      }
    } else {
      if (document.activeElement === this._focusLast) {
        e.preventDefault();
        this._focusFirst.focus();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Global handlers
  // ---------------------------------------------------------------------------

  _onDocClick(e) {
    if (e.target.closest('[data-cart-toggle]')) this.open();
  }

  // 'open' (default) | 'notify' | 'none' — set in theme.liquid's cart config.
  // Both ATC paths check it, so nothing opens behind the setting's back:
  // cart:opening fires on the click, cart:updated when the write lands. Under
  // 'notify' the only feedback is the count bubble's pop (_updateCountBadges).
  get _atcBehavior() {
    return window.theme?.settings?.cart?.atcBehavior || 'open';
  }

  // Called immediately when ATC is clicked — opens the drawer before the network
  // request completes so the user gets instant visual feedback.
  _onCartOpening() {
    if (this._atcBehavior === 'open') this.open();
  }

  _onCartUpdated(e) {
    if (this._atcBehavior === 'open' && !e?.detail?.silent) this.open();
    this.refresh();
  }

  // item 2 — BF-cache: browser returns to page with stale cart state
  _onPageshow(e) {
    if (!e.persisted) return;
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => this._updateCountBadges(cart.item_count))
      .catch(() => {});
  }

  // ---------------------------------------------------------------------------
  // Click delegation
  // ---------------------------------------------------------------------------

  _onClick(e) {
    // Close triggers: overlay, × button, "continue shopping" link
    if (e.target.closest('[data-drawer-close]')) { this.close(); return; }

    // Terms gate — CSS blocks pointer events on the disabled checkout button,
    // but keyboard activation still lands here.
    const gatedCheckout = e.target.closest('[data-cart-checkout][aria-disabled="true"]');
    if (gatedCheckout) {
      e.preventDefault();
      this.querySelector('[data-tnc-checkbox]')?.focus();
      return;
    }

    // Quantity stepper
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      this._changeQty(qtyBtn.dataset.qtyChange, parseInt(qtyBtn.dataset.qty, 10));
      return;
    }

    // Remove item
    const removeBtn = e.target.closest('[data-remove]');
    if (removeBtn) {
      e.preventDefault();
      this._changeQty(removeBtn.dataset.remove, 0);
      return;
    }

    // Upsell add
    const upsellBtn = e.target.closest('[data-upsell-add]');
    if (upsellBtn) {
      e.preventDefault();
      this._upsellAdd(parseInt(upsellBtn.dataset.upsellAdd, 10), upsellBtn);
      return;
    }

    // Customer Notes quick-bar toggle
    const notesTrigger = e.target.closest('[data-quick-notes-trigger]');
    if (notesTrigger) {
      const expanded = notesTrigger.getAttribute('aria-expanded') === 'true';
      notesTrigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      const body = document.getElementById(notesTrigger.getAttribute('aria-controls'));
      if (body) body.hidden = expanded;
      return;
    }

    // Discount popup toggle
    const discountTrigger = e.target.closest('[data-discount-popup-trigger]');
    if (discountTrigger) {
      const popup = document.getElementById(discountTrigger.getAttribute('aria-controls'));
      if (!popup) return;
      const open = !popup.hidden;
      popup.hidden = open;
      discountTrigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      return;
    }

    // Close discount popup when clicking outside it
    if (!e.target.closest('.cart-discount-popup') && !e.target.closest('[data-discount-popup-trigger]')) {
      const popup = this.querySelector('.cart-discount-popup');
      if (popup && !popup.hidden) {
        popup.hidden = true;
        const trigger = this.querySelector('[data-discount-popup-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    }

    // Accordion toggle — only one open at a time
    const accordionTrigger = e.target.closest('.cart-accordion__trigger');
    if (accordionTrigger) {
      const expanded = accordionTrigger.getAttribute('aria-expanded') === 'true';
      // Close all other accordions in the drawer
      this.querySelectorAll('.cart-accordion__trigger[aria-expanded="true"]').forEach(other => {
        if (other === accordionTrigger) return;
        other.setAttribute('aria-expanded', 'false');
        const otherId = other.getAttribute('aria-controls');
        if (otherId) {
          const otherBody = document.getElementById(otherId);
          if (otherBody) otherBody.hidden = true;
        }
      });
      // Toggle the clicked one
      accordionTrigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      const bodyId = accordionTrigger.getAttribute('aria-controls');
      if (bodyId) {
        const body = document.getElementById(bodyId);
        if (body) body.hidden = expanded;
      }
      return;
    }
  }

  // ---------------------------------------------------------------------------
  // Input delegation
  // ---------------------------------------------------------------------------

  _onInput(e) {
    // Qty direct input
    const qtyInput = e.target.closest('[data-qty-input]');
    if (qtyInput) {
      clearTimeout(this._qtyInputTimer);
      this._qtyInputTimer = setTimeout(() => {
        const val = parseInt(qtyInput.value, 10);
        if (!isNaN(val) && val >= 1) this._changeQty(qtyInput.dataset.qtyInput, val);
        else qtyInput.value = qtyInput.dataset.prevQty || 1;
      }, 700);
      return;
    }

    // Order note (item 23)
    const noteField = e.target.closest('[data-cart-note]');
    if (noteField) {
      clearTimeout(this._noteTimer);
      this._noteTimer = setTimeout(() => this._persistNote(noteField.value), 600);
      return;
    }

    // Cart attributes (item 23)
    const attrField = e.target.closest('[data-cart-attribute]');
    if (attrField) {
      const key = attrField.dataset.cartAttribute;
      clearTimeout(this._attrTimers[key]);
      this._attrTimers[key] = setTimeout(
        () => this._persistAttributes({ [key]: attrField.value }),
        600
      );
    }
  }

  _onChange(e) {
    if (e.target.closest('[data-tnc-checkbox]')) {
      this._syncTermsGate();
      return;
    }

    // Qty direct input — fires on blur or Enter
    const qtyInput = e.target.closest('[data-qty-input]');
    if (qtyInput) {
      clearTimeout(this._qtyInputTimer);
      const val = parseInt(qtyInput.value, 10);
      if (!isNaN(val) && val >= 1) this._changeQty(qtyInput.dataset.qtyInput, val);
      else qtyInput.value = qtyInput.dataset.prevQty || 1;
      return;
    }

    // select and checkbox cart attribute fields fire 'change', not 'input' (item 29)
    const attrField = e.target.closest('[data-cart-attribute]');
    if (!attrField) return;
    const key   = attrField.dataset.cartAttribute;
    const value = attrField.type === 'checkbox'
      ? (attrField.checked ? attrField.value : '')
      : attrField.value;
    clearTimeout(this._attrTimers[key]);
    this._attrTimers[key] = setTimeout(() => this._persistAttributes({ [key]: value }), 300);
  }

  // ---------------------------------------------------------------------------
  // Submit delegation
  // ---------------------------------------------------------------------------

  _onSubmit(e) {
    // Postcode saved — swap the gate message over to "select a date"
    if (e.target.closest('cart-postcode-checker')) { this._syncTermsGate(); return; }

    const discountForm = e.target.closest('[data-discount-form]');
    if (!discountForm) return;
    e.preventDefault();
    const input = discountForm.querySelector('.cart-discount__input');
    const code  = input ? input.value.trim().toUpperCase() : '';
    this._applyDiscount(discountForm, code);
  }

  // ---------------------------------------------------------------------------
  // Cart API helpers
  // ---------------------------------------------------------------------------

  // Flush any debounced note / attribute writes before a cart mutation so the
  // following SRA re-render picks up the current field values (item 23).
  async _flushPendingWrites() {
    const writes = [];

    if (this._noteTimer) {
      clearTimeout(this._noteTimer);
      this._noteTimer = null;
      const el = this.querySelector('[data-cart-note]');
      if (el) writes.push(this._persistNote(el.value));
    }

    for (const [key, timer] of Object.entries(this._attrTimers)) {
      clearTimeout(timer);
      delete this._attrTimers[key];
      const el = this.querySelector(`[data-cart-attribute="${CSS.escape(key)}"]`);
      if (el) writes.push(this._persistAttributes({ [key]: el.value }));
    }

    if (writes.length) await Promise.allSettled(writes);
  }

  async _changeQty(lineKey, qty) {
    this._setLoading(true);
    this._clearItemError(lineKey);
    await this._flushPendingWrites();

    try {
      const res  = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ id: lineKey, quantity: qty }),
      });
      const data = await res.json();

      if (!res.ok) {
        const msg = data.description || data.message || window.theme?.strings?.cart?.errorUpdateQuantity || 'Could not update quantity.';
        // 422 = inventory limit; show next to the specific item (item 18, 25)
        if (res.status === 422) this._showItemError(lineKey, msg);
        else                    this._showBannerError(msg);
        this._setLoading(false);
        return;
      }

      await this.refresh();
    } catch {
      this._showBannerError(window.theme?.strings?.cart?.errorUpdate || 'Unable to update cart. Please try again.');
      this._setLoading(false);
    }
  }

  async _upsellAdd(variantId, btn) {
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    await this._flushPendingWrites();

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || window.theme?.strings?.cart?.errorAdd || 'Could not add product.');
      }
      const item = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { item } }));
    } catch (err) {
      this._showBannerError(err.message);
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.innerHTML = label;
    }
  }

  async _persistNote(note) {
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ note }),
      });
    } catch {}
  }

  async _persistAttributes(attributes) {
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ attributes }),
      });
    } catch {}
  }

  _applyDiscount(form, code) {
    const feedback = form.querySelector('.cart-discount__feedback');
    if (!code) {
      if (feedback) {
        feedback.textContent = (window.theme?.strings?.cart?.discountCodeRequired || 'Please enter a discount code.');
        feedback.className   = 'cart-discount__feedback cart-discount__feedback--error';
        feedback.hidden      = false;
      }
      return;
    }
    this._discountCode = code;
    this._applyDiscountToCheckout(code);
    if (feedback) {
      feedback.textContent = (window.theme?.strings?.cart?.discountCodePending || 'Code "[code]" will be applied at checkout.').replace('[code]', code);
      feedback.className   = 'cart-discount__feedback cart-discount__feedback--success';
      feedback.hidden      = false;
    }
  }

  _applyDiscountToCheckout(code) {
    if (!code) return;
    this.querySelectorAll('[data-cart-checkout]').forEach(btn => {
      try {
        const url = new URL(btn.href, window.location.origin);
        url.searchParams.set('discount', code);
        btn.href = url.pathname + url.search;
      } catch {}
    });
  }

  // ---------------------------------------------------------------------------
  // Section Rendering API refresh (items 3, 6, 11, 15, 31, 32)
  // ---------------------------------------------------------------------------

  async refresh() {
    if (this._refreshing) return;
    this._refreshing = true;
    this._setLoading(true);

    try {
      // The cart page renders this same section markup at /cart, where '/' would
      // return the index page's sections instead — data-section-url names the
      // URL the section actually lives on.
      const res  = await fetch(`${this.dataset.sectionUrl || '/'}?sections=${this._sectionId}`);
      const data = await res.json();
      const html = data[this._sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace extras floating panel
      const freshExtras   = doc.querySelector('.cart-extras-panel');
      const currentExtras = this.querySelector('.cart-extras-panel');
      const mainPanel     = this.querySelector('.cart-drawer__panel');
      if      (freshExtras && currentExtras)  currentExtras.replaceWith(freshExtras);
      else if (freshExtras && !currentExtras && mainPanel) this.insertBefore(freshExtras, mainPanel);
      else if (!freshExtras && currentExtras) currentExtras.remove();

      // Replace progress bar (now lives outside the scrollable body)
      const freshBar   = doc.querySelector('.cart-progress-bar');
      const currentBar = this.querySelector('.cart-progress-bar');
      if      (freshBar && currentBar)   currentBar.replaceWith(freshBar);
      else if (freshBar && !currentBar) {
        const panel = this.querySelector('.cart-drawer__panel');
        const body  = this.querySelector('.cart-drawer__body');
        if (panel && body) panel.insertBefore(freshBar, body);
      }
      else if (!freshBar && currentBar)  currentBar.remove();

      // Replace scrollable body
      const freshBody   = doc.querySelector('.cart-drawer__body');
      const currentBody = this.querySelector('.cart-drawer__body');
      if (freshBody && currentBody) currentBody.replaceWith(freshBody);

      // Replace / insert / remove sticky footer
      const freshFooter   = doc.querySelector('.cart-drawer__footer');
      const currentFooter = this.querySelector('.cart-drawer__footer');
      if      (freshFooter && currentFooter)  currentFooter.replaceWith(freshFooter);
      else if (freshFooter && !currentFooter) this.querySelector('.cart-drawer__panel').appendChild(freshFooter);
      else if (!freshFooter && currentFooter) currentFooter.remove();

      // Re-apply stored discount to the new checkout button(s) (item 12)
      if (this._discountCode) this._applyDiscountToCheckout(this._discountCode);

      // The replaced footer/extras markup ships in its default state, so re-apply
      // the behaviour those regions used to carry as inline scripts.
      this._syncTermsGate();
      this._loadUpsellRecommendations();

      // Sync all count badges
      const freshDrawer = doc.querySelector('cart-drawer');
      const count = freshDrawer ? parseInt(freshDrawer.dataset.itemCount, 10) || 0 : 0;
      this._updateCountBadges(count);

      // Re-init Shopify dynamic checkout (Shop Pay, Apple Pay, Google Pay) (item 12)
      window.Shopify?.PaymentButton?.init?.();

      // item 26: let third-party apps (BNPL widgets, wishlist, review badges)
      // know the drawer DOM was replaced so they can re-initialise their widgets.
      document.dispatchEvent(new CustomEvent('cart-drawer:rendered', {
        bubbles: true,
        detail: { cartDrawer: this },
      }));

      if (this.getAttribute('aria-hidden') === 'false') this._updateFocusTrap();

    } catch {
      this._showBannerError(window.theme?.strings?.cart?.errorRefresh || 'Unable to refresh cart. Please reload the page.');
    } finally {
      this._setLoading(false);
      this._refreshing = false;
    }

  }

  // ---------------------------------------------------------------------------
  // Terms & conditions gate
  // ---------------------------------------------------------------------------

  // Mirrors the checkbox onto the checkout button's aria-disabled state; the
  // opacity / pointer-events treatment itself lives in cart-drawer.css.
  // Also gates on the postcode + delivery date row, where it renders.
  _syncTermsGate() {
    const checkbox = this.querySelector('[data-tnc-checkbox]');
    const accepted = !checkbox || checkbox.checked;
    const date     = this.querySelector('[data-ship-date-input]');
    const postcode = !!this.querySelector('cart-postcode-checker')?.getPostcodeForRates?.();
    const delivery = !date || (postcode && !!date.value);

    this.querySelectorAll('[data-cart-checkout]').forEach(btn => {
      if (accepted && delivery) btn.removeAttribute('aria-disabled');
      else                      btn.setAttribute('aria-disabled', 'true');
    });

    const msg = this.querySelector('[data-cart-gate-msg]');
    if (msg) {
      msg.hidden = delivery;
      if (!delivery) {
        const hasDate = !!date.value;
        msg.textContent = !postcode && !hasDate ? msg.dataset.msgBoth
                        : !postcode            ? msg.dataset.msgPostcode
                        :                        msg.dataset.msgDate;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Algorithmic upsell row (item 32) — only for the 'related' source, where the
  // rows come from Shopify's recommendations endpoint instead of Liquid.
  // ---------------------------------------------------------------------------

  async _loadUpsellRecommendations() {
    const el = this.querySelector('[data-upsell-ai]');
    if (!el || el.dataset.upsellLoaded === '1') return;
    el.dataset.upsellLoaded = '1';

    const list    = el.querySelector('.cart-upsell__list');
    const loading = el.querySelector('.cart-upsell__loading');
    const max     = parseInt(el.dataset.upsellMax, 10) || 4;

    try {
      const cart = typeof window.ccGetCartSnapshot === 'function'
        ? await window.ccGetCartSnapshot()
        : await fetch('/cart.js').then(r => r.json());

      const cartIds = (cart?.items || []).map(item => item.product_id);
      if (!cartIds.length) { el.hidden = true; return; }

      // Locale-aware root so multi-language stores get localised recommendations
      const root = window.Shopify?.routes?.root || '/';
      const url  = `${root}recommendations/products.json?product_id=${cartIds[cartIds.length - 1]}&limit=10&intent=related`;

      const data     = await fetch(url).then(r => r.json());
      const filtered = (data?.products || []).filter(p => !cartIds.includes(p.id));

      loading?.remove();
      if (!filtered.length || !list) { el.hidden = true; return; }

      filtered.slice(0, max).forEach(product => list.appendChild(this._buildUpsellRow(product)));
      list.hidden = false;
    } catch {
      el.hidden = true;
    }
  }

  _buildUpsellRow(product) {
    const compareAt = product.compare_at_price || 0;
    const pct       = compareAt > product.price
      ? Math.round(((compareAt - product.price) * 100) / compareAt)
      : 0;
    // The recommendations endpoint returns image URLs as plain strings (and a
    // featured_image string), not objects — reading .src here left every card
    // with no thumbnail. Size is inserted before the extension, keeping ?v=.
    const rawThumb = product.featured_image || product.images?.[0]?.src || product.images?.[0] || '';
    const thumb = typeof rawThumb === 'string'
      ? rawThumb.replace(/(\.(?:jpe?g|png|gif|webp))(\?|$)/i, '_200x200$1$2')
      : '';

    const li = document.createElement('li');
    li.className = 'cart-upsell__item';

    // Built from nodes rather than an HTML string so product titles can never
    // be interpreted as markup.
    if (thumb) {
      const imgLink = document.createElement('a');
      imgLink.href      = `/products/${product.handle}`;
      imgLink.className = 'cart-upsell__img-link';
      imgLink.tabIndex  = -1;
      imgLink.setAttribute('aria-hidden', 'true');

      const img = document.createElement('img');
      img.src     = thumb;
      img.alt     = '';
      img.width   = 72;
      img.height  = 72;
      img.loading = 'lazy';
      imgLink.appendChild(img);

      if (pct > 0) {
        const tag = document.createElement('span');
        tag.className   = 'cart-upsell__tag';
        tag.textContent = `${pct}% Off`;
        imgLink.appendChild(tag);
      }
      li.appendChild(imgLink);
    }

    const info = document.createElement('div');
    info.className = 'cart-upsell__info';

    const title = document.createElement('a');
    title.href        = `/products/${product.handle}`;
    title.className   = 'cart-upsell__title';
    title.textContent = product.title;
    info.appendChild(title);

    const variantTitle = product.variants?.[0]?.title;
    if (variantTitle && variantTitle !== 'Default Title') {
      const variant = document.createElement('span');
      variant.className   = 'cart-upsell__variant';
      variant.textContent = variantTitle;
      info.appendChild(variant);
    }

    const price = document.createElement('span');
    price.className   = 'cart-upsell__price';
    price.textContent = this._money(product.price);
    if (pct > 0) {
      const compare = document.createElement('s');
      compare.className   = 'cart-upsell__compare';
      compare.textContent = this._money(compareAt);
      price.appendChild(compare);
    }
    info.appendChild(price);
    li.appendChild(info);

    const add = document.createElement('button');
    add.type      = 'button';
    add.className = 'cart-upsell__add';
    add.dataset.upsellAdd = product.variants[0].id;
    add.setAttribute('aria-label', `Add ${product.title}`);
    add.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M9 3v12M3 9h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    li.appendChild(add);

    return li;
  }

  _money(cents) {
    if (window.Skeleton?.money) return window.Skeleton.money(cents);
    return ((Number(cents) || 0) / 100).toFixed(2);
  }

  // ---------------------------------------------------------------------------
  // State helpers
  // ---------------------------------------------------------------------------

  _setLoading(on) { this.classList.toggle('is-loading', on); }

  _showBannerError(msg) {
    const el = this.querySelector('.cart-drawer__errors');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => { el.hidden = true; }, 4000);
  }

  _showItemError(lineKey, msg) {
    const item = this.querySelector(`.cart-item[data-key="${CSS.escape(lineKey)}"]`);
    const el   = item?.querySelector('.cart-item__error');
    if (el) { el.textContent = msg; el.hidden = false; }
  }

  _clearItemError(lineKey) {
    const item = this.querySelector(`.cart-item[data-key="${CSS.escape(lineKey)}"]`);
    const el   = item?.querySelector('.cart-item__error');
    if (el) el.hidden = true;
  }

  _updateCountBadges(count) {
    const headerCount = this.querySelector('.cart-drawer__count');
    if (headerCount) headerCount.textContent = `(${count})`;
    document.querySelectorAll('[data-cart-item-count]').forEach(el => {
      // Pop the bubble only when the count goes up — a removal should not
      // celebrate. The class is stripped on animationend so it can re-fire.
      const grew = count > (parseInt(el.dataset.count, 10) || 0);
      el.textContent = String(count);
      el.dataset.count = String(count);
      if (!grew) return;
      el.classList.remove('is-bumped');
      void el.offsetWidth; // restart the animation on a rapid second add
      el.classList.add('is-bumped');
      el.addEventListener('animationend', () => el.classList.remove('is-bumped'), { once: true });
    });
    // Update cart toggle aria-label so screen readers announce the new count
    const toggle = document.querySelector('[data-cart-toggle]');
    if (toggle) {
      const base = toggle.dataset.cartLabel || toggle.getAttribute('aria-label')?.replace(/\s*\(\d+\)$/, '') || 'Cart';
      toggle.setAttribute('aria-label', `${base} (${count})`);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
// Called by cart-delivery-date-picker.js whenever the date (or postcode reset) changes.
window.syncCartDrawerCheckoutGate = () => document.querySelectorAll('cart-drawer').forEach(d => d._syncTermsGate());

// =============================================================================
// Feature extensions — all gated by window.theme.settings.cart
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
  const cfg = window.theme?.settings?.cart || {};

  // ---------------------------------------------------------------------------
  // Open / close custom events (cart:open, cart:close)
  // ---------------------------------------------------------------------------
  if (cfg.customEventsEnabled !== false) {
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      const observer = new MutationObserver(mutations => {
        mutations.forEach(m => {
          if (m.attributeName !== 'aria-hidden') return;
          const hidden = drawerEl.getAttribute('aria-hidden') === 'true';
          document.dispatchEvent(new CustomEvent(hidden ? 'cart:close' : 'cart:open', {
            bubbles: true,
            detail: { cartDrawer: drawerEl }
          }));
        });
      });
      observer.observe(drawerEl, { attributes: true });
    }
  }

  // ---------------------------------------------------------------------------
  // Haptic feedback
  // ---------------------------------------------------------------------------
  if (cfg.hapticEnabled && navigator.vibrate) {
    document.addEventListener('cart:updated',       () => navigator.vibrate(40));
    document.addEventListener('click', e => {
      if (e.target.closest('[data-qty-change]') || e.target.closest('[data-remove]')) {
        navigator.vibrate(20);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Superscript cents — transforms money strings in the drawer
  // ---------------------------------------------------------------------------
  if (cfg.superscriptCentsEnabled) {
    function applySuperscriptCents(root) {
      root.querySelectorAll(
        '[data-cart-subtotal], .cart-item__price, .cart-item__original-price, .cart-upsell__price'
      ).forEach(el => {
        if (el.dataset.centsApplied) return;
        el.dataset.centsApplied = '1';
        el.innerHTML = el.textContent.replace(
          /([£$€])(\d+)\.(\d{2})/g,
          '$1$2<sup style="font-size:0.65em;vertical-align:super;line-height:0">$3</sup>'
        );
      });
    }
    document.addEventListener('cart-drawer:rendered', () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) applySuperscriptCents(drawer);
    });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) applySuperscriptCents(drawer);
  }

  // ---------------------------------------------------------------------------
  // Scroll position preservation
  // ---------------------------------------------------------------------------
  if (cfg.scrollPreserveEnabled !== false) {
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      let savedScroll = 0;
      drawerEl.addEventListener('click', e => {
        if (e.target.closest('[data-qty-change]') || e.target.closest('[data-remove]')) {
          const body = drawerEl.querySelector('.cart-drawer__body');
          savedScroll = body ? body.scrollTop : 0;
        }
      });
      document.addEventListener('cart-drawer:rendered', () => {
        const body = drawerEl.querySelector('.cart-drawer__body');
        if (body && savedScroll) {
          requestAnimationFrame(() => { body.scrollTop = savedScroll; savedScroll = 0; });
        }
      });
    }
  }

  // ---------------------------------------------------------------------------
  // on:line-item:change custom event
  // ---------------------------------------------------------------------------
  if (cfg.lineItemEventEnabled !== false) {
    document.addEventListener('click', e => {
      const qtyBtn    = e.target.closest('[data-qty-change]');
      const removeBtn = e.target.closest('[data-remove]');
      if (!qtyBtn && !removeBtn) return;
      const key = qtyBtn ? qtyBtn.dataset.qtyChange : removeBtn.dataset.remove;
      const qty = qtyBtn ? parseInt(qtyBtn.dataset.qty, 10) : 0;
      document.dispatchEvent(new CustomEvent('on:line-item:change', {
        bubbles: true,
        detail: { key, quantity: qty }
      }));
    }, { capture: true });
  }

  // ---------------------------------------------------------------------------
  // Upsell arrows — page the recommendation row one viewport at a time.
  // Delegated on document so it survives the drawer re-rendering.
  // ---------------------------------------------------------------------------
  document.addEventListener('click', e => {
    const nav = e.target.closest('[data-upsell-nav]');
    if (!nav) return;
    const list = nav.closest('.cart-upsell')?.querySelector('.cart-upsell__list');
    if (!list) return;
    const step = list.clientWidth;
    list.scrollBy({ left: nav.dataset.upsellNav === 'next' ? step : -step, behavior: 'smooth' });
  });

  // ---------------------------------------------------------------------------
  // Clear cart button
  // ---------------------------------------------------------------------------
  document.addEventListener('click', async e => {
    if (!e.target.closest('[data-clear-cart]')) return;
    if (!confirm(window.theme?.strings?.cart?.confirmClear || 'Remove all items from your cart?')) return;
    const drawerEl = document.querySelector('cart-drawer');
    try {
      const res = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: {} }));
      }
    } catch {}
  });

});
